import { Box } from '@mui/material';
import React, { useMemo } from 'react';
import { H } from '../../Layout';
import { useStyling } from '../../Theme';
import type { ActionType } from '../ActionType';
import ResponsiveActionIconButton from './ResponsiveActionIconButton';
import { ResponsiveActionsProps } from './ResponsiveActions';

export interface InlineResponsiveActionsProps extends Required<
    Pick<ResponsiveActionsProps, 'showBorder' | 'containerSx' | 'iconSx' | 'isActivated'>
> {
    actions: ActionType[];
}

export const InlineResponsiveActions = ({
    actions,
    showBorder,
    containerSx,
    iconSx,
    isActivated
}: InlineResponsiveActionsProps) => {
    const { isMobile } = useStyling();
    const isAnyActionActive = actions.some((action) => action.loading);
    const areAllActionsHidden = actions.every((action) => action.hidden);
    const layout = showBorder ? 'bordered' : 'tooltip';
    const actionButtons = useMemo(
        () =>
            actions.map((action, index) => (
                <ResponsiveActionIconButton
                    key={index}
                    action={action}
                    index={index}
                    layout={layout}
                    iconSx={iconSx}
                    isAnyActionActive={isAnyActionActive}
                />
            )),
        [actions, iconSx, isAnyActionActive, layout]
    );

    return showBorder ? (
        <Box
            component="div"
            sx={{
                position: 'absolute',
                display: areAllActionsHidden ? 'none' : 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                right: '5px',
                top: '5px',
                px: isMobile ? 0 : 1,
                py: 0,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.primary',
                borderRadius: '18px',
                height: '42px',
                visibility: !isActivated ? 'hidden' : 'visible',
                ...containerSx
            }}
        >
            <H sx={{ width: 'unset' }}>{actionButtons}</H>
        </Box>
    ) : (
        <H
            sx={{
                visibility: !isActivated ? 'hidden' : 'visible',
                width: 'unset',
                backgroundColor: 'background.paper',
                borderRadius: '18px',
                ...containerSx
            }}
        >
            {actionButtons}
        </H>
    );
};
